import { useState, useCallback } from 'react';
import { useServerInfiniteScroll } from '../hooks/useServerInfiniteScroll';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { FileAudio, Upload, Trash2 } from 'lucide-react';
import { Track } from '../data/mockData';
import { toast } from 'sonner';
import JSZip from 'jszip';
import { API_BASE_URL, useListTracksPaginatedQuery } from '../../store/api';

interface FilesTabProps {
  channelId: string | number;
  stationName: string;
  channelName: string;
  onUploadTrack: (file: File) => Promise<void>;
  onDeleteTrack: (trackId: string | number) => void;
}

export function FilesTab({
  channelId,
  stationName,
  channelName,
  onUploadTrack,
  onDeleteTrack,
}: FilesTabProps) {
  const [pageOffset, setPageOffset] = useState(0);
  const PAGE_LIMIT = 20;

  const { data: tracksData, isFetching } = useListTracksPaginatedQuery({
    channelId: Number(channelId),
    offset: pageOffset,
    limit: PAGE_LIMIT,
  });

  const tracks: Track[] = (tracksData?.items ?? []).map((t: any) => ({
    ...t,
    id: String(t.id),
    channelId: String(t.channelId ?? t.channel_id),
    fileName: t.fileName ?? t.file_name,
    uploadDate: t.uploadDate ?? t.upload_date,
  }));

  const loadMore = useCallback(() => {
    if (tracksData?.hasMore && !isFetching) {
      setPageOffset((prev) => prev + PAGE_LIMIT);
    }
  }, [tracksData?.hasMore, isFetching]);

  const { sentinelRef } = useServerInfiniteScroll({
    hasMore: tracksData?.hasMore ?? false,
    isFetching,
    onLoadMore: loadMore,
  });

  const [dragActive, setDragActive] = useState(false);
  const [selectedTrackIds, setSelectedTrackIds] = useState<Set<string | number>>(
    new Set()
  );
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'single' | 'bulk'; track?: Track } | null>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
    e.target.value = ''; // Reset input
  };

  const handleFileUpload = async (file: File) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.mp3')) {
      toast.error('Only MP3 files are allowed');
      return;
    }

    // Validate file size (200 MB = 200 * 1024 * 1024 bytes)
    const maxSize = 200 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size must be less than 200 MB');
      return;
    }

    toast.loading('Validating file...', { id: 'upload' });

    try {
      await onUploadTrack(file);
      toast.success('File uploaded and validated successfully', { id: 'upload' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed.';
      toast.error(message, { id: 'upload' });
    }
  };

  const sanitizeSegment = (value: string) =>
    value
      .trim()
      .replace(/[^a-zA-Z0-9._-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 80);

  const buildFolderName = () => {
    const safeStation = sanitizeSegment(stationName || 'station');
    const safeChannel = sanitizeSegment(channelName || 'channel');
    return `${safeStation}_${safeChannel}`;
  };

  const sanitizeFileName = (fileName: string) => fileName.replace(/[\\/]+/g, '_');

  const toggleTrackSelection = (trackId: string | number) => {
    setSelectedTrackIds((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedTrackIds.size === tracks.length) {
      setSelectedTrackIds(new Set());
      return;
    }

    setSelectedTrackIds(new Set(tracks.map((track) => track.id)));
  };

  const handleDownloadSelected = async () => {
    if (selectedTrackIds.size === 0) {
      toast.error('Select at least one file to download');
      return;
    }

    const selectedTracks = tracks.filter((track) => selectedTrackIds.has(track.id));
    toast.loading(`Downloading ${selectedTracks.length} file(s)...`, { id: 'download' });

    try {
      const zip = new JSZip();
      const folderName = buildFolderName();
      const folder = zip.folder(folderName);
      if (!folder) {
        throw new Error('Failed to create download folder.');
      }

      for (const track of selectedTracks) {
        const response = await fetch(`${API_BASE_URL}/tracks/${track.id}/download`);
        if (!response.ok) {
          throw new Error(`Failed to download ${track.fileName}`);
        }
        const blob = await response.blob();
        folder.file(sanitizeFileName(track.fileName), blob);
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${folderName}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Download complete', { id: 'download' });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Download failed.';
      toast.error(message, { id: 'download' });
    }
  };

  const handleDelete = (track: Track) => {
    setDeleteConfirm({ type: 'single', track });
  };

  const handleDeleteSelected = () => {
    if (selectedTrackIds.size === 0) {
      toast.error('Select at least one file to delete');
      return;
    }
    setDeleteConfirm({ type: 'bulk' });
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'single' && deleteConfirm.track) {
      onDeleteTrack(deleteConfirm.track.id);
      toast.success(`${deleteConfirm.track.fileName} deleted`);
    } else if (deleteConfirm.type === 'bulk') {
      selectedTrackIds.forEach((trackId) => onDeleteTrack(trackId));
      setSelectedTrackIds(new Set());
      toast.success('Selected files deleted');
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white mb-2">Files</h2>
          <p className="text-gray-400">Upload and manage MP3 files for your programs</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={handleDownloadSelected}
            disabled={selectedTrackIds.size === 0}
            className="border-gray-700 text-black hover:text-white hover:bg-gray-800 text-xs sm:text-sm"
          >
            Download
          </Button>
          <Button
            variant="outline"
            onClick={handleDeleteSelected}
            disabled={selectedTrackIds.size === 0}
            className="border-gray-700 text-black hover:text-white hover:bg-gray-800 text-xs sm:text-sm"
          >
            Delete
          </Button>
        </div>
      </div>

      {/* Upload Area */}
      <div className="mb-8">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 sm:p-12 text-center transition-colors ${
            dragActive
              ? 'border-blue-500 bg-blue-950/30'
              : 'border-gray-700 bg-gray-800'
          }`}
        >
          <Upload className="size-12 mx-auto mb-4 text-gray-500" />
          <h3 className="text-lg font-semibold text-white mb-2">
            Drop MP3 files here or click to upload
          </h3>
          <p className="text-sm text-gray-400 mb-4">
            Maximum file size: 200 MB • Required: 48 kHz sampling rate, ~128 kbps bitrate
          </p>
          <input
            type="file"
            id="file-upload"
            accept=".mp3"
            onChange={handleFileInput}
            className="hidden"
          />
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white">
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="mr-2 size-4" />
              Upload MP3
            </label>
          </Button>
        </div>

        <div className="mt-4 p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
          <p className="text-sm text-yellow-200">
            <strong>Upload Requirements:</strong> MP3 files must have 48 kHz sampling rate
            and approximately 128 kbps average bitrate. Files not meeting these
            requirements will be rejected.
          </p>
        </div>
      </div>

      {/* Tracks Table */}
      {tracks.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <FileAudio className="size-12 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400 text-lg">No files uploaded yet</p>
          <p className="text-gray-500 text-sm mt-2">Upload your first MP3 file to get started</p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-800 bg-gray-900 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400 w-12">
                  <input
                    type="checkbox"
                    checked={tracks.length > 0 && selectedTrackIds.size === tracks.length}
                    onChange={toggleSelectAll}
                    aria-label="Select all files"
                  />
                </TableHead>
                <TableHead className="text-gray-400">File Name</TableHead>
                <TableHead className="text-gray-400 text-center hidden md:table-cell">Playback</TableHead>
                <TableHead className="text-gray-400 hidden sm:table-cell">Size</TableHead>
                <TableHead className="text-gray-400 hidden sm:table-cell">Duration</TableHead>
                <TableHead className="text-gray-400 hidden lg:table-cell">Uploaded On</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tracks.map((track) => (
                <TableRow key={track.id} className="border-gray-800 hover:bg-gray-800">
                  <TableCell className="text-gray-400">
                    <input
                      type="checkbox"
                      checked={selectedTrackIds.has(track.id)}
                      onChange={() => toggleTrackSelection(track.id)}
                      aria-label={`Select ${track.fileName}`}
                    />
                  </TableCell>
                  <TableCell className="text-white font-medium">
                    <div className="flex items-center gap-2">
                      <FileAudio className="size-4 text-gray-500" />
                      {track.fileName}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-400 hidden md:table-cell">
                    <div className="flex justify-center">
                      <div className="w-full max-w-[380px] rounded-lg border border-gray-700 bg-gray-800/80 px-3 py-2">
                        <audio
                          controls
                          preload="metadata"
                          src={`${API_BASE_URL}/tracks/${track.id}/download`}
                          className="h-10 w-full [color-scheme:dark]"
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-400 hidden sm:table-cell">{track.size}</TableCell>
                  <TableCell className="text-gray-400 hidden sm:table-cell">{track.duration}</TableCell>
                  <TableCell className="text-gray-400 hidden lg:table-cell">{track.uploadDate}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(track)}
                        className="text-gray-400 hover:text-red-400 hover:bg-gray-800"
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {(tracksData?.hasMore ?? false) && (
        <div ref={sentinelRef} className="flex justify-center py-6">
          <div className="text-sm text-gray-500">Loading more...</div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              {deleteConfirm?.type === 'single' && deleteConfirm.track
                ? `Are you sure you want to delete "${deleteConfirm.track.fileName}"? This action uses a soft delete.`
                : `Are you sure you want to delete ${selectedTrackIds.size} selected file(s)? This action uses a soft delete.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}